import React, { useState, useEffect, useCallback } from 'react';
import { 
  Award, ArrowRight, Sparkles, Phone, Zap, Star, 
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, 
  X, CheckCircle, AlertTriangle 
} from 'lucide-react';
import { useTheme } from './ThemeContext';
// Import the contact form submission API service
import { submitContactForm } from '../../../src/services/contactApi'; 

// --- INTERNAL POPUP COMPONENT (Logic Preserved) ---
const CounselorPopup = ({ showCounselorPopup, setShowCounselorPopup, isDarkMode }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: 'Counselor Request - Specializations Page',
    country: 'India',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', null

  if (!showCounselorPopup) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setSubmissionStatus(null); 
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmissionStatus(null);
    
    if (!formData.firstName || !formData.phone) {
        setSubmissionStatus('error');
        setLoading(false);
        return;
    }

    try {
      await submitContactForm(formData); 
      setSubmissionStatus('success');
      setFormData({
        firstName: '', lastName: '', email: '', phone: '',
        subject: 'Counselor Request - Specializations Page',
        country: formData.country, message: '',
      });
      setTimeout(() => setShowCounselorPopup(false), 3000);
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmissionStatus('error');
    } finally {
      setLoading(false);
    }
  };
  
  const inputStyle = `w-full p-3 rounded-lg border transition-all duration-200 ${
    isDarkMode 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCounselorPopup(false)} />
      <div className={`relative w-full max-w-lg rounded-2xl shadow-2xl transition-all duration-300 transform scale-100 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`sticky top-0 z-10 flex items-center justify-between p-5 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-xl font-bold inline-flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <Phone className="w-5 h-5 text-blue-500" /> Request a Counselor
          </h2>
          <button onClick={() => setShowCounselorPopup(false)} className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Fill out your details for a free, personalized consultation.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="First Name *" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputStyle} />
                <input type="text" placeholder="Last Name" name="lastName" value={formData.lastName} onChange={handleChange} className={inputStyle} />
            </div>
            <input type="tel" placeholder="Phone Number *" name="phone" value={formData.phone} onChange={handleChange} required className={inputStyle} />
            <input type="email" placeholder="Email Address" name="email" value={formData.email} onChange={handleChange} className={inputStyle} />
            <select name="country" value={formData.country} onChange={handleChange} className={inputStyle}>
                <option value="India">India</option><option value="USA">USA</option><option value="Canada">Canada</option><option value="UK">UK</option><option value="Other">Other</option>
            </select>
            <input type="hidden" name="subject" value={formData.subject} />
            <textarea placeholder="Message (Optional)" name="message" value={formData.message} onChange={handleChange} rows="3" className={inputStyle} />
            {submissionStatus === 'success' && (
                <div className="flex items-center gap-2 p-3 bg-green-500/20 text-green-600 rounded-lg">
                    <CheckCircle className="w-5 h-5" /> <span className="text-sm font-medium">Request submitted successfully! We will contact you shortly.</span>
                </div>
            )}
            {submissionStatus === 'error' && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 text-red-600 rounded-lg">
                    <AlertTriangle className="w-5 h-5" /> <span className="text-sm font-medium">Failed to submit. Please check required fields (Name, Phone).</span>
                </div>
            )}
            <button type="submit" disabled={loading || submissionStatus === 'success'} className={`w-full py-3 rounded-lg font-bold text-white transition-all duration-300 flex items-center justify-center gap-2 ${loading || submissionStatus === 'success' ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'}`}>
                {loading ? <span className="animate-spin h-5 w-5 border-4 border-white border-t-transparent rounded-full"></span> : submissionStatus === 'success' ? 'Scheduled!' : <><Phone className="w-4 h-4" /> Schedule My Call</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// --- MAIN SPECIALIZATIONS COMPONENT ---
export default function Specializations({ isVisible }) {
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [showCounselorPopup, setShowCounselorPopup] = useState(false);

  // --- CONTENT DATA (Strictly Preserved) ---
  const domains = [
    { name: "Technology & Engineering", icon: "⚙️", gradient: "from-blue-500 to-cyan-500", specs: ["Computer Science & IT", "Software Engineering", "Data Science & Analytics", "Artificial Intelligence"], allSpecs: ["Computer Science & IT", "Software Engineering", "Data Science & Analytics", "Artificial Intelligence", "Cybersecurity", "Information Systems", "Electronics & Communication", "Mechanical Engineering", "Civil Engineering", "Electrical Engineering", "Aerospace Engineering", "Chemical Engineering", "Industrial Engineering", "Biomedical Engineering", "Environmental Engineering", "Materials Science", "Robotics & Automation", "Telecommunications"] },
    { name: "Business & Management", icon: "💼", gradient: "from-purple-500 to-pink-500", specs: ["Business Administration (MBA)", "Finance & Banking", "Marketing & Sales", "Human Resources"], allSpecs: ["Business Administration (MBA)", "Finance & Banking", "Marketing & Sales", "Human Resources", "International Business", "Operations Management", "Supply Chain Management", "Entrepreneurship", "Strategic Management", "Organizational Behavior", "Business Analytics", "Risk Management", "Investment Banking", "Corporate Finance", "Retail Management", "Project Management", "Digital Marketing", "Brand Management"] },
    { name: "Healthcare & Medicine", icon: "🏥", gradient: "from-red-500 to-orange-500", specs: ["Medicine (MBBS)", "Nursing", "Pharmacy", "Dentistry"], allSpecs: ["Medicine (MBBS)", "Nursing", "Pharmacy", "Dentistry", "Public Health", "Medical Laboratory Technology", "Physiotherapy", "Occupational Therapy", "Radiology", "Cardiology", "Neurology", "Pediatrics", "Dermatology", "Oncology", "Psychiatry", "Anesthesiology", "Emergency Medicine", "Clinical Research"] },
    { name: "Arts & Design", icon: "🎨", gradient: "from-pink-500 to-rose-500", specs: ["Fine Arts", "Graphic Design", "Fashion Design", "Interior Design"], allSpecs: ["Fine Arts", "Graphic Design", "Fashion Design", "Interior Design", "Animation", "Industrial Design", "Product Design", "Visual Communication", "Typography", "Photography", "Illustration", "UI/UX Design", "Game Design", "Textile Design", "Jewelry Design", "Ceramics", "Sculpture", "Digital Arts"] },
    { name: "Science & Research", icon: "🔬", gradient: "from-green-500 to-emerald-500", specs: ["Physics", "Chemistry", "Biology", "Mathematics"], allSpecs: ["Physics", "Chemistry", "Biology", "Mathematics", "Biotechnology", "Microbiology", "Genetics", "Biochemistry", "Zoology", "Botany", "Environmental Science", "Marine Biology", "Astronomy", "Geology", "Statistics", "Applied Mathematics", "Nanotechnology", "Molecular Biology"] },
    { name: "Social Sciences & Humanities", icon: "📚", gradient: "from-indigo-500 to-purple-500", specs: ["Psychology", "Sociology", "Political Science", "International Relations"], allSpecs: ["Psychology", "Sociology", "Political Science", "International Relations", "History", "Philosophy", "Anthropology", "Economics", "Geography", "Linguistics", "Cultural Studies", "Gender Studies", "Development Studies", "Social Work", "Public Administration", "Journalism", "Mass Communication", "Film Studies"] },
    { name: "Education & Teaching", icon: "🎓", gradient: "from-yellow-500 to-amber-500", specs: ["Education (B.Ed/M.Ed)", "Early Childhood Education", "Special Education", "Educational Technology"], allSpecs: ["Education (B.Ed/M.Ed)", "Early Childhood Education", "Special Education", "Educational Technology", "Curriculum Development", "Educational Psychology", "Adult Education", "Distance Learning", "Teacher Training", "School Administration", "Educational Leadership", "Literacy Studies", "Mathematics Education", "Science Education", "Language Education", "Music Education", "Physical Education", "Counseling"] },
    { name: "Law & Legal Studies", icon: "⚖️", gradient: "from-gray-600 to-gray-800", specs: ["Law (LLB/LLM)", "Corporate Law", "Criminal Law", "Constitutional Law"], allSpecs: ["Law (LLB/LLM)", "Corporate Law", "Criminal Law", "Constitutional Law", "International Law", "Human Rights Law", "Environmental Law", "Tax Law", "Intellectual Property Law", "Family Law", "Labor Law", "Commercial Law", "Cyber Law", "Banking Law", "Immigration Law", "Real Estate Law", "Patent Law", "Legal Studies"] },
    { name: "Agriculture & Food Sciences", icon: "🌾", gradient: "from-lime-500 to-green-600", specs: ["Agriculture", "Horticulture", "Animal Science", "Veterinary Science"], allSpecs: ["Agriculture", "Horticulture", "Animal Science", "Veterinary Science", "Agricultural Engineering", "Agronomy", "Soil Science", "Plant Pathology", "Food Technology", "Dairy Science", "Poultry Science", "Fisheries", "Agricultural Economics", "Crop Science", "Seed Technology", "Organic Farming", "Precision Agriculture", "Food Safety"] },
    { name: "Hospitality & Tourism", icon: "🏨", gradient: "from-teal-500 to-cyan-600", specs: ["Hotel Management", "Tourism Management", "Event Management", "Culinary Arts"], allSpecs: ["Hotel Management", "Tourism Management", "Event Management", "Culinary Arts", "Restaurant Management", "Food & Beverage Management", "Front Office Management", "Housekeeping Management", "Resort Management", "Travel & Tourism", "Aviation & Hospitality", "Cruise Management", "Heritage Tourism", "Eco-Tourism", "Adventure Tourism", "Catering Management", "Bakery & Confectionery", "Hotel Operations"] },
    { name: "Music & Performing Arts", icon: "🎵", gradient: "from-violet-500 to-purple-600", specs: ["Music Performance", "Music Composition", "Music Theory", "Music Production"], allSpecs: ["Music Performance", "Music Composition", "Music Theory", "Music Production", "Classical Music", "Jazz Studies", "Vocal Performance", "Instrumental Music", "Music Education", "Music Therapy", "Sound Engineering", "Audio Production", "Theatre Arts", "Dance", "Drama", "Stage Management", "Choreography", "Film Music"] },
    { name: "Sports & Physical Education", icon: "⚽", gradient: "from-orange-500 to-red-600", specs: ["Physical Education", "Sports Science", "Kinesiology", "Exercise Physiology"], allSpecs: ["Physical Education", "Sports Science", "Kinesiology", "Exercise Physiology", "Sports Management", "Sports Medicine", "Athletic Training", "Sports Psychology", "Coaching", "Fitness Training", "Sports Nutrition", "Biomechanics", "Recreation Management", "Yoga Studies", "Sports Marketing", "Sports Journalism", "Adapted Physical Education", "Sports Rehabilitation"] }
  ];

  const scrollToSectionTop = useCallback(() => {
    const section = document.getElementById('animate-domains');
    if (section) {
      const offset = section.getBoundingClientRect().top + window.scrollY - 100; 
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    const getItemsPerPage = (width) => {
      if (width >= 1024) return 3; 
      if (width >= 768) return 2;  
      return 1; 
    };

    const handleResize = () => {
      const newItemsPerPage = getItemsPerPage(window.innerWidth);
      setItemsPerPage(prevItems => {
        if (prevItems !== newItemsPerPage) {
          setCurrentIndex(0); 
          setIsAutoPlaying(true);
          return newItemsPerPage;
        }
        return prevItems;
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + itemsPerPage >= domains.length ? 0 : prev + itemsPerPage));
    }, 4000); 
    return () => clearInterval(interval);
  }, [isAutoPlaying, domains.length, itemsPerPage]);
  
  // Effect to control body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = showCounselorPopup ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCounselorPopup]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + itemsPerPage >= domains.length ? 0 : prev + itemsPerPage));
    setIsAutoPlaying(false);
    scrollToSectionTop(); 
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - itemsPerPage < 0 
      ? Math.max(0, Math.floor((domains.length - 1) / itemsPerPage) * itemsPerPage) 
      : prev - itemsPerPage));
    setIsAutoPlaying(false);
    scrollToSectionTop(); 
  };

  const handleDotClick = (idx) => {
    setCurrentIndex(idx * itemsPerPage);
    setIsAutoPlaying(false);
    scrollToSectionTop(); 
  };

  const toggleExpand = (idx) => {
    setExpandedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
  };
  
  const handleBadgeClick = () => {
      window.location.href = '/student/programs';
  };
  
  const handleCardRedirection = (event, isExpandButton) => {
    if (!isExpandButton) {
      handleBadgeClick();
    }
  };
  
  const handleCounselorClick = () => {
    setShowCounselorPopup(true);
  };

  const visibleDomains = domains.slice(currentIndex, currentIndex + itemsPerPage);
  const totalPages = Math.ceil(domains.length / itemsPerPage);

  return (
    <>
      <section 
        id="animate-domains" 
        // FIX: Reduced top padding (pt-4) to remove excess whitespace
        className={`relative pt-4 pb-12 sm:pb-20 lg:pb-24 px-4 sm:px-6 transition-colors duration-500 ${
          isDarkMode ? 'bg-gradient-to-br from-black via-gray-900 to-black' : 'bg-gradient-to-br from-white via-gray-50 to-white'
        }`}
      >
        {/* ... (Background effects) ... */}
        <div className={`absolute inset-0 ${isDarkMode ? 'opacity-10' : 'opacity-5'}`}>
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(${isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(0, 0, 0, 0.1)'} 1px, transparent 1px),
                             linear-gradient(90deg, ${isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(0, 0, 0, 0.1)'} 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'gridMove 20s linear infinite'
          }}></div>
        </div>
        {isDarkMode && (
          <>
            <div className="absolute top-1/3 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-purple-600 rounded-full blur-[120px] opacity-20 animate-pulse"></div>
            <div className="absolute bottom-1/3 right-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-pink-600 rounded-full blur-[120px] opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
          </>
        )}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full ${isDarkMode ? 'bg-purple-400' : 'bg-black'}`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 20}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: isDarkMode ? Math.random() * 0.5 + 0.3 : Math.random() * 0.2 + 0.1
            }}
          />
        ))}

        <div className="max-w-7xl mx-auto relative z-10">
          <div className={`text-center mb-12 sm:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            
            <div 
              className="inline-block mb-4 sm:mb-6 relative group cursor-pointer" 
              onClick={handleBadgeClick} 
            >
              <div className={`absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 rounded-full blur ${
                isDarkMode ? 'opacity-30' : 'opacity-20'
              } group-hover:opacity-60 transition duration-1000 animate-pulse`}></div>
              <span className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white px-4 py-2 sm:px-8 sm:py-3 rounded-full text-xs sm:text-sm font-bold shadow-lg inline-flex items-center gap-2">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" style={{ animationDuration: '3s' }} />
                200+ SPECIALIZATIONS
                <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black mb-4 sm:mb-6">
              <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-text text-transparent drop-shadow-2xl">
                Academic Domains We Cover
              </span>
            </h2>
            <p className={`text-base sm:text-xl max-w-3xl mx-auto font-light ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Explore 12 major academic domains with access to 10,000+ universities worldwide
            </p>
          </div>

          {/* Carousel */}
          <div 
            className="relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
            onTouchStart={() => setIsAutoPlaying(false)}
            onTouchEnd={() => setIsAutoPlaying(true)}
          >
            <div className={`grid gap-8 ${itemsPerPage === 1 ? 'grid-cols-1' : itemsPerPage === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {visibleDomains.map((domain, idx) => {
                const actualIdx = currentIndex + idx;
                const isExpanded = expandedCards[actualIdx];
                
                const hiddenCount = domain.allSpecs.length - domain.specs.length;
                
                return (
                  <div
                    key={actualIdx}
                    className={`group relative backdrop-blur-xl rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border-2 h-[450px] sm:h-[500px] flex flex-col ${ 
                      isDarkMode ? 'bg-gray-800/50 border-gray-700/50 hover:border-transparent' : 'bg-white/80 border-gray-200 hover:border-transparent'
                    } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transitionDelay: `${idx * 80}ms` }}
                    onClick={(e) => handleCardRedirection(e, e.target.closest('button'))}
                  >
                    
                    {/* Card Header (toggle button stops propagation) */}
                    <div className={`relative p-4 sm:p-6 border-b-2 rounded-t-3xl ${
                      isDarkMode ? 'bg-gradient-to-r border-gray-700' : 'bg-gradient-to-r border-gray-200'
                    } ${domain.gradient}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3 invisible">
                          <ChevronLeft className="w-5 h-5 text-white" />
                          <ChevronRight className="w-5 h-5 text-white" />
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation(); 
                            toggleExpand(actualIdx);
                          }}
                          className="p-2 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-colors relative z-20"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-white" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-white" />
                          )}
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-4xl sm:text-5xl">{domain.icon}</div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-black text-white mb-1">{domain.name}</h3>
                          <p className="text-xs sm:text-sm text-white/90 font-semibold">18 specializations available</p>
                        </div>
                      </div>
                    </div>

                    {/* Card Body (includes scroll prompt) */}
                    <div className="relative flex-1 overflow-hidden">
                      <div className={`h-full overflow-y-auto p-4 sm:p-6 cursor-pointer ${ 
                        isDarkMode ? 'bg-gray-800/30' : 'bg-white/50'
                      }`}
                      style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: isDarkMode ? '#4B5563 transparent' : '#D1D5DB transparent'
                      }}>
                        <ul className="space-y-2 sm:space-y-3">
                          {(isExpanded ? domain.allSpecs : domain.specs).map((spec, i) => (
                            <li 
                              key={i} 
                              className={`flex items-start gap-3 p-2 sm:p-3 rounded-xl transition-all duration-300 ${
                                isDarkMode ? 'hover:bg-gray-700/30 text-gray-200' : 'hover:bg-gray-100 text-gray-800'
                              }`}
                            >
                              <span className={`mt-0.5 w-2 h-2 rounded-full bg-gradient-to-br ${domain.gradient} flex-shrink-0`}></span>
                              <span className="text-sm sm:text-base font-medium leading-relaxed">{spec}</span> 
                            </li>
                          ))}
                          {!isExpanded && hiddenCount > 0 && (
                            <li className="pt-3">
                              <div className={`text-center font-bold text-sm italic transition-all duration-300 ${
                                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                              }`}>
                                +{hiddenCount} More! Scroll to see or click to explore programs
                              </div>
                            </li>
                          )}
                        </ul>
                      </div>
                      
                      {!isExpanded && (
                        <div className={`absolute bottom-0 left-0 right-0 h-1/4 ${
                          isDarkMode ? 'bg-gradient-to-t from-gray-800/80 to-transparent' : 'bg-gradient-to-t from-white/90 to-transparent'
                        } pointer-events-none`}></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation Buttons and Dots (unchanged) */}
            <button 
              onClick={prevSlide} 
              className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 sm:-translate-x-4 lg:-translate-x-16 w-10 h-10 sm:w-14 sm:h-14 rounded-full ${
                isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-black/10 hover:bg-black/20'
              } backdrop-blur-md flex items-center justify-center transition-all z-10 shadow-xl`}
            >
              <ChevronLeft className={`w-5 h-5 sm:w-7 sm:h-7 ${isDarkMode ? 'text-white' : 'text-black'}`} />
            </button>

            <button 
              onClick={nextSlide} 
              className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 sm:translate-x-4 lg:-translate-x-16 w-10 h-10 sm:w-14 sm:h-14 rounded-full ${
                isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-black/10 hover:bg-black/20'
              } backdrop-blur-md flex items-center justify-center transition-all z-10 shadow-xl`}
            >
              <ChevronRight className={`w-5 h-5 sm:w-7 sm:h-7 ${isDarkMode ? 'text-white' : 'text-black'}`} />
            </button>

            <div className="flex justify-center gap-2 mt-8 sm:mt-12">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button 
                  key={idx} 
                  onClick={() => handleDotClick(idx)}
                  className={`h-2 rounded-full transition-all ${
                    Math.floor(currentIndex / itemsPerPage) === idx 
                      ? 'w-6 sm:w-8 bg-gradient-to-r from-blue-500 to-purple-500' 
                      : `w-2 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`
                  }`} 
                  aria-label={`Go to page ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className={`mt-16 sm:mt-20 relative overflow-hidden rounded-3xl shadow-xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className={`relative p-6 sm:p-8 text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 animate-fade-in">
              Find your interest               </h3>
              
              <p className="text-xs sm:text-sm mb-4 sm:mb-6 max-w-xl mx-auto leading-relaxed font-light">
                With access to 10,000+ universities across 30+ countries, we cover virtually every academic field
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {/* Explore All Programs Button (Redirects) */}
                <button 
                  className={`group px-5 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm relative overflow-hidden
                  ${isDarkMode 
                    ? 'bg-white text-blue-600' 
                    : 'bg-blue-600 text-white'
                  }`}
                  onClick={handleBadgeClick} 
                >
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'bg-blue-100/50' : 'bg-black/10'}`}></div>
                  <span className="relative z-10">Explore All Programs</span>
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform relative z-10" />
                </button>
                
                {/* Talk to Counselor Button (Loads Popup) */}
                <button 
                  onClick={handleCounselorClick} 
                  className={`group backdrop-blur-md border-2 px-5 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2 text-xs sm:text-sm relative overflow-hidden
                  ${isDarkMode 
                    ? 'bg-white/20 border-white text-white hover:bg-white/30' 
                    : 'bg-transparent border-blue-600 text-blue-600 hover:bg-blue-50'
                  }`}>
                  <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'bg-white/10' : 'bg-white/50'}`}></div>
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4 group-hover:rotate-12 transition-transform relative z-10" />
                  <span className="relative z-10">Talk to Counselor</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          /* CSS keyframes removed for brevity, assuming they are defined elsewhere or compiled correctly */
        `}</style>
      </section>
      
      {/* Render the controlled Popup Modal */}
      <CounselorPopup 
        showCounselorPopup={showCounselorPopup} 
        setShowCounselorPopup={setShowCounselorPopup}
        isDarkMode={isDarkMode}
      />
    </>
  );
}